import { Link } from "react-router-dom";
import { useDarkMode } from "../context/DarkModeContext";
import { Helmet } from "react-helmet-async";

export default function About() {
  const { isDark } = useDarkMode();

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDark
          ? 'bg-gradient-to-br from-gray-900 to-gray-800'
          : 'bg-gradient-to-br from-sky-50 to-blue-50'
      }`}
    >
      <Helmet>
        <title>About FindWork - Connecting Workers & Employers in Kenya</title>
        <meta
          name="description"
          content="FindWork is dedicated to connecting job seekers with meaningful opportunities across Kenya. No CVs, no delays — just real workers and real employers."
        />
        <link rel="canonical" href="https://findwork.ltd/about" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-sky-500 to-blue-600 text-white py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-extrabold mb-4">About FindWork</h1>
          <p className="text-lg text-sky-100 max-w-2xl mx-auto">
            FindWork is dedicated to empowering job seekers by connecting them
            with meaningful opportunities and helping companies discover the
            best talent.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div
            className={`p-8 rounded-xl shadow hover:shadow-lg transition-all duration-300 border ${
              isDark
                ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                : 'bg-white border-sky-50 hover:bg-gray-50'
            }`}
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent mb-3">
              Our Mission
            </h2>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              Our mission is simple: to make job searching stress-free and
              efficient. We provide a reliable platform where job seekers can
              find verified opportunities and employers can connect with
              qualified candidates seamlessly.
            </p>
          </div>

          <div
            className={`p-8 rounded-xl shadow hover:shadow-lg transition-all duration-300 border ${
              isDark
                ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                : 'bg-white border-sky-50 hover:bg-gray-50'
            }`}
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent mb-3">
              Our Vision
            </h2>
            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
              We envision a world where every person can access opportunities to
              grow their career and every company can hire the right talent
              without barriers. FindWork is committed to shaping the future of
              recruitment.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section
        className={`py-16 transition-colors duration-300 ${
          isDark
            ? 'bg-gradient-to-br from-gray-800 to-gray-700'
            : 'bg-gradient-to-br from-sky-100 to-blue-100'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2
            className={`text-3xl font-bold mb-6 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            Meet Our Team
          </h2>
          <p
            className={`max-w-2xl mx-auto mb-12 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            Behind FindWork is a passionate team of professionals dedicated to
            innovation, technology, and career development. Together, we are
            building the bridge between job seekers and employers.
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            {/* Team Member 1 */}
            <div
              className={`rounded-xl shadow p-6 border hover:shadow-lg transition-all duration-300 ${
                isDark
                  ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  : 'bg-white border-sky-50 hover:bg-gray-50'
              }`}
            >
              <img
                src="/jobhunt5-removebg-preview.png"
                alt="Team Member"
                className="w-24 h-24 mx-auto rounded-full mb-4"
              />
              <h3
                className={`text-xl font-semibold ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}
              >
                Jane Doe
              </h3>
              <p className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium">
                CEO & Founder
              </p>
            </div>

            {/* Team Member 2 */}
            <div
              className={`rounded-xl shadow p-6 border hover:shadow-lg transition-all duration-300 ${
                isDark
                  ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  : 'bg-white border-sky-50 hover:bg-gray-50'
              }`}
            >
              <img
                src="/jobhunt2-removebg-preview.png"
                alt="Team Member"
                className="w-24 h-24 mx-auto rounded-full mb-4"
              />
              <h3
                className={`text-xl font-semibold ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}
              >
                John Smith
              </h3>
              <p className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium">
                Tech Lead
              </p>
            </div>

            {/* Team Member 3 */}
            <div
              className={`rounded-xl shadow p-6 border hover:shadow-lg transition-all duration-300 ${
                isDark
                  ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  : 'bg-white border-sky-50 hover:bg-gray-50'
              }`}
            >
              <img
                src="/jobhunt4-removebg-preview.png"
                alt="Team Member"
                className="w-24 h-24 mx-auto rounded-full mb-4"
              />
              <h3
                className={`text-xl font-semibold ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}
              >
                Mary Johnson
              </h3>
              <p className="bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent font-medium">
                Community Manager
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-sky-500 to-blue-600 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">Join Us on the Journey</h2>
        <p className="mb-6 max-w-2xl mx-auto text-blue-100">
          Whether you're a job seeker or an employer, FindWork is here to
          support your growth and success.
        </p>
        <div className="flex justify-center space-x-4">
          <Link
            to="/find-job"
            className="bg-white text-sky-600 px-6 py-3 rounded-lg font-medium shadow hover:shadow-lg transition-all duration-300 border border-white hover:bg-white/10 hover:text-white"
          >
            Find Jobs
          </Link>
          <Link
            to="/packages"
            className="border border-white text-white px-6 py-3 rounded-lg font-medium shadow hover:shadow-lg transition-all duration-300 hover:bg-white/10"
          >
            View Packages
          </Link>
        </div>
      </section>
    </div>
  );
}
